import React, { useRef, useState } from "react";
import Checkbox from "@mui/material/Checkbox";

const useTableBulk = (
  totalIds,
  { canRemove = true, orderedIds = totalIds, fetchTotalIds } = {}
) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const lastSelectedIdRef = useRef(null);
  const [bulkState, setBulkState] = useState(false);
  const selectAllRequestRef = useRef(0);

  const toggleBulkState = () => {
    const newState = !bulkState;
    setBulkState(newState);
    lastSelectedIdRef.current = null;
    selectAllRequestRef.current += 1;

    if (!newState) {
      setSelectedIds([]);
      return;
    }

    // Callers that no longer preload every id (assetsCollection) hand us a
    // fetcher; ids arrive after the click instead of on every page load.
    if (totalIds.length > 0 || !fetchTotalIds) {
      setSelectedIds(totalIds);
      return;
    }

    const request = selectAllRequestRef.current;
    fetchTotalIds()
      .then(ids => {
        if (request !== selectAllRequestRef.current) return;
        setSelectedIds(ids || []);
      })
      .catch(() => {
        if (request !== selectAllRequestRef.current) return;
        setBulkState(false);
        setSelectedIds([]);
      });
  };

  const toggleSelectedId = (entityId, event) => {
    const isShift = event && event.shiftKey;
    const rangeAnchorId = lastSelectedIdRef.current;
    lastSelectedIdRef.current = entityId;

    setSelectedIds(prev => {
      let newSelectedIds;

      if (isShift && rangeAnchorId) {
        const start = orderedIds.indexOf(rangeAnchorId);
        const end = orderedIds.indexOf(entityId);

        if (start !== -1 && end !== -1) {
          const min = Math.min(start, end);
          const max = Math.max(start, end);
          const range = orderedIds.slice(min, max + 1);
          const selectedSet = new Set(prev);
          range.forEach(id => selectedSet.add(id));
          newSelectedIds = Array.from(selectedSet);
        }
      }

      if (!newSelectedIds) {
        newSelectedIds = prev.includes(entityId)
          ? prev.filter(id => id !== entityId)
          : [...prev, entityId];
      }

      setBulkState(false);
      return newSelectedIds;
    });
  };

  const label = canRemove ? <Checkbox checked={bulkState} onClick={toggleBulkState} /> : null;

  const unSelectBulk = () => {
    setBulkState(false);
    setSelectedIds([]);
    lastSelectedIdRef.current = null;
  };

  const Component = ({ entity }) => {
    if (!canRemove) {
      return null;
    }

    return (
      <Checkbox
        checked={selectedIds.includes(entity.id)}
        onClick={e => {
          e.stopPropagation();
          toggleSelectedId(entity.id, e);
        }}
      />
    );
  };

  return {
    selectedIds,
    unSelectBulk,
    props: {
      label: label,
      component: Component,
    },
  };
};

export default useTableBulk;
